/**
 * Coarse analytic fields (spec §4) — pure functions over planet-local unit
 * direction. They READ the existing PlanetSampler (terrain contract), never
 * duplicate it. Every constant here is a tuning knob (spec §12); v1 values
 * chosen to make terran worlds habitable and barren worlds near-empty.
 */
import { PlanetSampler, type PlanetArchetype, type TerrainSample } from "../universe.js";
import * as pmath from "../pmath.js";
import { hashInts, h01 } from "./hash.js";
import { type CellId, cellsPerFace, LEVEL_REGION, packCell } from "./cells.js";
import { faceDir } from "../cubesphere.js";
import { hashSeed } from "../rng.js";
import { tierRegistryFor } from "./tierRegistry.js";

/** Civilization weight per archetype — scales seed density and suitability. */
const CIV_WEIGHT: Record<PlanetArchetype, number> = {
  terran: 1.0,
  ocean: 0.35,
  desert: 0.3,
  ice: 0.2,
  volcanic: 0.15,
  barren: 0.05,
};

export function civWeight(archetype: PlanetArchetype): number {
  return CIV_WEIGHT[archetype];
}

// Knobs (spec §12).
const SLOPE_EPS_M = 200;        // finite-difference step for macro slope, meters
const SLOPE_PENALTY = 1;        // per unit macro-slope (amp-units/radian; land avg ≈ 4
                                // on the terran subject) — 1/(1+slope): halves at slope 1
const RIVER_NEAR_M = 400;       // river adjacency bonus band — real distance query, not carve reach
const RIVER_BONUS = 0.35;
const COAST_BAND = 0.06;        // amplitude units above sea counted "coastal"
const COAST_BONUS = 0.3;
const TEMP_IDEAL = 0.0;         // temp field runs ~[-1,1] (land avg ≈ -0.6 on the subject)
const TEMP_TOL = 0.5;
const MOIST_FLOOR = 0.15;

const scratch: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };

/** Macro slope magnitude of continentalH via tangent finite differences. */
function macroSlope(sampler: PlanetSampler, dx: number, dy: number, dz: number, t1: number[], t2: number[]): number {
  const e = SLOPE_EPS_M / sampler.def.radius; // radians ≈ chord for small e
  const h = (px: number, py: number, pz: number): number => {
    const il = 1 / pmath.hypot(px, py, pz);
    return sampler.continentalH(px * il, py * il, pz * il);
  };
  const g1 = (h(dx + t1[0] * e, dy + t1[1] * e, dz + t1[2] * e) - h(dx - t1[0] * e, dy - t1[1] * e, dz - t1[2] * e)) / (2 * e);
  const g2 = (h(dx + t2[0] * e, dy + t2[1] * e, dz + t2[2] * e) - h(dx - t2[0] * e, dy - t2[1] * e, dz - t2[2] * e)) / (2 * e);
  return pmath.hypot(g1, g2);
}

/** A stable tangent basis at dir (matches the frames.ts convention: pick the
 *  axis least aligned with dir, orthonormalize). */
export function tangentBasis(dx: number, dy: number, dz: number, t1: number[], t2: number[]): void {
  const ax = Math.abs(dx), ay = Math.abs(dy), az = Math.abs(dz);
  let rx = 0, ry = 0, rz = 0;
  if (ax <= ay && ax <= az) rx = 1;
  else if (ay <= az) ry = 1;
  else rz = 1;
  // t1 = normalize(r × dir), t2 = dir × t1
  let x = ry * dz - rz * dy, y = rz * dx - rx * dz, z = rx * dy - ry * dx;
  const il = 1 / pmath.hypot(x, y, z);
  t1[0] = x * il; t1[1] = y * il; t1[2] = z * il;
  t2[0] = dy * t1[2] - dz * t1[1];
  t2[1] = dz * t1[0] - dx * t1[2];
  t2[2] = dx * t1[1] - dy * t1[0];
}

const T1: number[] = [0, 0, 0];
const T2: number[] = [0, 0, 0];

/** Habitability ∈ [0,1] (spec §4): slope, water, climate, archetype. */
export function suitabilityAt(sampler: PlanetSampler, dx: number, dy: number, dz: number): number {
  sampler.sample(dx, dy, dz, scratch, true, false, true); // climate on, towns off
  if((scratch.lavaDepthM??0)>0)return 0;
  // Underwater rejection — but a no-sea planet (seaRel === 0; universe.ts sets
  // this when def.seaRadius is null) has NO sea: its sub-datum lowlands are dry
  // land, not water, so guard the reject the same way the coastal test below does
  // (sampler.seaRel !== 0). Without this, ~half of a no-sea planet's land (all of
  // it below the datum) is spuriously suppressed to suitability 0.
  if (sampler.seaRel !== 0 && scratch.h <= sampler.seaRel) return 0;
  tangentBasis(dx, dy, dz, T1, T2);
  const slope = macroSlope(sampler, dx, dy, dz, T1, T2);
  let s = 1 / (1 + SLOPE_PENALTY * slope);
  // Water access.
  const coastal = scratch.h - sampler.seaRel < COAST_BAND && sampler.seaRel !== 0;
  const nearRiver = sampler.rivers().nearestRiverDistM(dx, dy, dz, RIVER_NEAR_M) !== null;
  s *= 1 + (coastal ? COAST_BONUS : 0) + (nearRiver ? RIVER_BONUS : 0);
  // Climate: temperate band, not bone-dry.
  const dt = (scratch.temp - TEMP_IDEAL) / TEMP_TOL;
  s *= pmath.exp(-dt * dt);
  s *= MOIST_FLOOR + (1 - MOIST_FLOOR) * Math.min(1, Math.max(0, scratch.moist) * 1.6);
  s *= civWeight(sampler.def.archetype);
  return Math.min(1, Math.max(0, s));
}

// Area-averaged candidate scoring (W2d-2 rider 1): score a candidate by the mean
// suitability of a small fixed cross around it, so a contiguous buildable site
// outranks an isolated suitability spike. Offset in meters along the chart axes.
const AREA_SAMPLE_M = 35;
const AT1: number[] = [0, 0, 0];
const AT2: number[] = [0, 0, 0];

/**
 * Area-averaged suitability (spec §4 rider): the mean of 5 {@link suitabilityAt}
 * samples — the candidate point plus 4 neighbours at ±AREA_SAMPLE_M along the two
 * chart tangent axes (fixed cross, positions derived from the SAME candidate
 * direction, no new salts). Lifts broad buildable ground over single-point spikes.
 * Used wherever candidates are scored (tierRegistry enumeration + waystation deal),
 * so settlementSeeds (which caches the dealt suit) stays consistent.
 */
export function areaSuitabilityAt(sampler: PlanetSampler, dx: number, dy: number, dz: number): number {
  tangentBasis(dx, dy, dz, AT1, AT2);
  const e = AREA_SAMPLE_M / sampler.def.radius;
  const at = (ax: number[], s: number): number => {
    const px = dx + ax[0] * s, py = dy + ax[1] * s, pz = dz + ax[2] * s;
    const il = 1 / pmath.hypot(px, py, pz);
    return suitabilityAt(sampler, px * il, py * il, pz * il);
  };
  // Sample the neighbours first: suitabilityAt clobbers the module T1/T2 scratch,
  // but AT1/AT2 are captured here and read only inside `at`, so the cross is stable.
  const s = suitabilityAt(sampler, dx, dy, dz)
    + at(AT1, e) + at(AT1, -e) + at(AT2, e) + at(AT2, -e);
  return s / 5;
}

// Buildability probe (tier-deal gate): the fraction of a settlement-scale disc
// whose BUILDING-scale slope exceeds the street grade wall. macroSlope (200 m FD
// on continentalH) is blind to ridge/mountain detail that lives in sample()'s
// finer octaves — a mountainside can read near-flat at macro scale (observed:
// areaSuit 0.87 on ground where 44% of the disc exceeds the grade limit, which
// starves the street grower into braided contour strands). Deterministic fixed
// grid, no hashing.
const STEEP_GRID_M = 30;   // sample spacing (~1.5 street segments)
const STEEP_FD_M = 6;      // finite-difference half-step at building scale
const STEEP_LIMIT = 0.47;  // = streetgrow SECONDARY_MAX_GRADE: ground streets can't use

/**
 * Fraction of a radius-`radiusM` disc around `dir` whose 12 m-FD slope exceeds
 * the secondary-street grade wall — the share of the site the street grower
 * cannot build on. ~230 full samples at the default radius; callers memoize.
 */
export function steepFracAt(sampler: PlanetSampler, dx: number, dy: number, dz: number, radiusM: number): number {
  tangentBasis(dx, dy, dz, AT1, AT2);
  const R = sampler.def.radius;
  const hAt = (mu: number, mv: number): number => {
    const px = dx + (AT1[0] * mu + AT2[0] * mv) / R;
    const py = dy + (AT1[1] * mu + AT2[1] * mv) / R;
    const pz = dz + (AT1[2] * mu + AT2[2] * mv) / R;
    const il = 1 / pmath.hypot(px, py, pz);
    sampler.sample(px * il, py * il, pz * il, scratch, false, false, true);
    return scratch.h * sampler.amplitude;
  };
  let over = 0, n = 0;
  for (let mu = -radiusM; mu <= radiusM; mu += STEEP_GRID_M) {
    for (let mv = -radiusM; mv <= radiusM; mv += STEEP_GRID_M) {
      if (mu * mu + mv * mv > radiusM * radiusM) continue;
      const g = pmath.hypot(hAt(mu + STEEP_FD_M, mv) - hAt(mu - STEEP_FD_M, mv),
                            hAt(mu, mv + STEEP_FD_M) - hAt(mu, mv - STEEP_FD_M)) / (2 * STEEP_FD_M);
      n++;
      if (g > STEEP_LIMIT) over++;
    }
  }
  return over / n;
}

/**
 * True iff the surface at `dir` is open water (sample height ≤ sea datum). Mirrors
 * {@link suitabilityAt}'s underwater guard; a no-sea planet (seaRel === 0) is never
 * wet. Used by the waystation dry-slot re-roll (W2d-2 rider 2).
 */
export function isWetAt(sampler: PlanetSampler, dx: number, dy: number, dz: number): boolean {
  if (sampler.seaRel === 0&&sampler.def.archetype!=='volcanic') return false;
  sampler.sample(dx, dy, dz, scratch, false, false, true);
  return (scratch.lavaDepthM??0)>0||(sampler.seaRel!==0&&scratch.h <= sampler.seaRel);
}

/**
 * Arterial direction field (spec §4): the macro-terrain gradient rotated 90°
 * in the tangent plane — roads follow contours. (Seed attraction blends in
 * during W2 growth, where the seeds are known; the base field is enough for
 * W1 acceptance.) Always writes a unit tangent, even on flats (hash-free
 * deterministic fallback: t1 of the stable basis).
 */
export function arterialDirAt(sampler: PlanetSampler, dx: number, dy: number, dz: number, out: number[] | Float64Array): void {
  tangentBasis(dx, dy, dz, T1, T2);
  const e = SLOPE_EPS_M / sampler.def.radius;
  const h = (px: number, py: number, pz: number): number => {
    const il = 1 / pmath.hypot(px, py, pz);
    return sampler.continentalH(px * il, py * il, pz * il);
  };
  const g1 = (h(dx + T1[0] * e, dy + T1[1] * e, dz + T1[2] * e) - h(dx - T1[0] * e, dy - T1[1] * e, dz - T1[2] * e)) / (2 * e);
  const g2 = (h(dx + T2[0] * e, dy + T2[1] * e, dz + T2[2] * e) - h(dx - T2[0] * e, dy - T2[1] * e, dz - T2[2] * e)) / (2 * e);
  // Rotate gradient (g1, g2) by 90° in the tangent plane → contour direction.
  let cx = -g2, cy = g1;
  const len = pmath.hypot(cx, cy);
  if (len < 1e-12) { cx = 1; cy = 0; } else { cx /= len; cy /= len; }
  out[0] = T1[0] * cx + T2[0] * cy;
  out[1] = T1[1] * cx + T2[1] * cy;
  out[2] = T1[2] * cx + T2[2] * cy;
}

// --- Settlement seeds (spec §4) --------------------------------------------

export interface SettlementSeed {
  /** Unit direction, planet-local. */
  dx: number; dy: number; dz: number;
  /** Subcell index within the owning region cell (0..SUB²-1) — part of the
   *  settlement id AND the address of its candidate geometry (see candidateDir). */
  k: number;
  /**
   * Settlement tier (W2c-1 spec): 0 = waystation, 1 = hamlet, 2 = village,
   * 3 = town, 4 = city, 5 = metropolis. Replaces the old 0/1/2 sizeClass; the
   * value is dealt from the per-planet quota deck (see tierRegistry.ts) and drives
   * skeleton growth, parcel radii, and city walls DIRECTLY via TIER_PARAMS[tier]
   * (the Task-2 remap — the legacy 0/1/2 size bridge is retired).
   */
  tier: number;
  /**
   * AREA-AVERAGED suitability at the seed (the mean of a small fixed cross —
   * {@link areaSuitabilityAt}, W2d-2 rider 1), cached for consumers. Despite the
   * name it is NOT the bare point {@link suitabilityAt}; popCurve consumes it as
   * the settlement's CARRYING CAPACITY (K ∝ suit), so a contiguous buildable site
   * sustains a larger population than an isolated suitability spike.
   */
  suit: number;
  /**
   * Per-settlement deviation slot (spec §"One deviation slot"): 0 = no rule-break,
   * else a hash-rolled rule-breaker id 1..4 (data-only in W2c-1; dealt by the
   * TierRegistry). Serialized into the CityGraph header when this seed is the
   * region's primary settlement.
   */
  deviation: number;
}

export const SETTLEMENT_SUB = 4;   // SUB×SUB jittered subgrid per region cell
const SUB = SETTLEMENT_SUB;

/**
 * The unit direction of subcell `k` (0..SUB²-1) inside region `cell` — the
 * canonical settlement candidate geometry. Extracted verbatim from the original
 * settlementSeeds subgrid so the TierRegistry and settlementSeeds agree on every
 * candidate position (the "positions unchanged" invariant). `n` is
 * cellsPerFace(radius, LEVEL_REGION). Jitter salt 0xa11, indices k*2 / k*2+1 —
 * byte-identical to the pre-registry `salt = sy*SUB+sx` scheme.
 */
export function candidateDir(planetHash: number, cell: CellId, n: number, k: number, out: number[] | Float64Array): void {
  const w = 2 / n;                 // cell width in uv
  const u0 = -1 + w * cell.x;
  const v0 = -1 + w * cell.y;
  const sw = w / SUB;              // subcell width
  const sx = k % SUB;
  const sy = (k / SUB) | 0;
  const ju = h01(hashInts(planetHash, 0xa11, cell.face, cell.x, cell.y, k * 2));
  const jv = h01(hashInts(planetHash, 0xa11, cell.face, cell.x, cell.y, k * 2 + 1));
  const u = u0 + sw * (sx + 0.2 + 0.6 * ju);
  const v = v0 + sw * (sy + 0.2 + 0.6 * jv);
  faceDir(cell.face, u, v, out);
}

/** Stable per-seed hash — the settlement's identity in int form. */
export function seedHash(planetHash: number, cell: CellId, k: number): number {
  return hashInts(planetHash, 0x5eed, cell.face, cell.x, cell.y, k);
}

/**
 * The settlements of a region cell, as SettlementSeeds (W2c-1). This is now a
 * VIEW over the per-planet TierRegistry (built once per sampler, cached): the
 * cell's dealt tier assignments are turned back into seeds, each position
 * recomputed from its (cell, k) candidate geometry so positions are byte-identical
 * to the pre-registry jittered subgrid. Per-cell purity is traded for per-planet
 * determinism — a worker rebuilds the registry identically from the same pure
 * inputs (documented cost: the first settlementSeeds call on a sampler builds the
 * whole-planet registry). Non-habitable planets carry waystations only.
 */
export function settlementSeeds(sampler: PlanetSampler, cell: CellId): SettlementSeed[] {
  // Grid math and salts key on (face, x, y) only; a province cell would silently
  // reuse the region cell's seeds. Enforce the region-cell contract.
  if (cell.level !== LEVEL_REGION) throw new Error(`settlementSeeds: region cell required, got level ${cell.level}`);
  const reg = tierRegistryFor(sampler);
  const list = reg.byCell.get(packCell(cell));
  if (list === undefined || list.length === 0) return [];
  const planetHash = hashSeed(sampler.def.seed);
  const n = cellsPerFace(sampler.def.radius, LEVEL_REGION);
  const sorted = [...list].sort((a, b) => a.k - b.k); // ascending-k growth contract
  const out: SettlementSeed[] = [];
  for (const a of sorted) {
    const dir: number[] = [0, 0, 0];
    candidateDir(planetHash, cell, n, a.k, dir);
    out.push({ dx: dir[0], dy: dir[1], dz: dir[2], k: a.k, tier: a.tier, suit: a.suit, deviation: a.deviation });
  }
  return out;
}

// --- Collapsed-history inputs (spec §4, consumed by W3) --------------------

export interface WorldEvent {
  yearsAgo: number;
  kind: "war" | "plague" | "boom";
  severity: number; // (0, 1]
}

const ERA_YEARS = 2000;   // history horizon
const EVENT_MEAN = 6;     // expected events per province per era
const EVENT_MAX = 16;

/** Hashed Poisson event schedule per province cell, sorted oldest-first. */
export function eventSchedule(planetHash: number, province: CellId): WorldEvent[] {
  // Poisson count via inverse-CDF walk on one uniform draw, bounded.
  const u = h01(hashInts(planetHash, 0xe7e, province.face, province.x, province.y, 0));
  let count = 0;
  let p = pmath.exp(-EVENT_MEAN);
  let cdf = p;
  while (u > cdf && count < EVENT_MAX) {
    count++;
    p = (p * EVENT_MEAN) / count;
    cdf += p;
  }
  const out: WorldEvent[] = [];
  for (let i = 0; i < count; i++) {
    const yearsAgo = h01(hashInts(planetHash, 0xe7e, province.face, province.x, province.y, 1 + i * 3)) * ERA_YEARS;
    const kindRoll = h01(hashInts(planetHash, 0xe7e, province.face, province.x, province.y, 2 + i * 3));
    const severity = 0.2 + 0.8 * h01(hashInts(planetHash, 0xe7e, province.face, province.x, province.y, 3 + i * 3));
    out.push({ yearsAgo, kind: kindRoll < 0.35 ? "war" : kindRoll < 0.6 ? "plague" : "boom", severity });
  }
  out.sort((a, b) => b.yearsAgo - a.yearsAgo);
  return out;
}

const POP_K_MAX = 4000;   // carrying-capacity ceiling (people) at suit = 1

/**
 * Closed-form population (spec §4): logistic toward carrying capacity.
 * Founding age and growth rate are hashed per settlement; population at any
 * epoch is O(1). yearsAgo counts back from the present (0 = now).
 */
export function popCurve(seedH: number, suit: number, yearsAgo: number): number {
  const founded = 200 + 1800 * h01(hashInts(seedH, 0xf0d));       // years ago
  if (yearsAgo >= founded) return 0;
  const K = Math.max(8, POP_K_MAX * suit);
  const r = 0.004 + 0.008 * h01(hashInts(seedH, 0x6a7e));          // 1/years
  const age = founded - yearsAgo;                                   // years since founding
  const mid = founded * 0.5;
  return K / (1 + pmath.exp(-r * (age - mid)));
}

/** Prosperity ∈ [0, ~2]: hashed base modulated by events older than t. */
export function prosperity(seedH: number, suit: number, yearsAgo: number, events: WorldEvent[]): number {
  let p = 0.5 + 0.5 * h01(hashInts(seedH, 0x9c05)) + 0.5 * suit;
  for (const e of events) {
    if (e.yearsAgo <= yearsAgo) continue; // hasn't happened yet at epoch t
    const since = e.yearsAgo - yearsAgo;
    const decay = pmath.exp(-since / 400);
    p *= e.kind === "boom" ? 1 + 0.3 * e.severity * decay : 1 - 0.35 * e.severity * decay;
  }
  return Math.min(2, Math.max(0, p));
}
